// contexts/LanguageContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { useAdminApi } from '../api/adminApi';
import { LanguageResp } from '../types/language';

const cookies = new Cookies();

interface LanguageContextType {
  language: string;
  languages: LanguageResp[];
  translations: Record<string, string>;
  setLanguage: (lang: string) => void;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  languages: [],
  translations: {},
  setLanguage: () => {},
  loading: true,
});

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const api = useAdminApi();
  const [language, setLanguage] = useState<string>('en');
  const [languages, setLanguages] = useState<LanguageResp[]>([]);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Mevcut dili yükle (cookie'den veya varsayılan) - SADECE İLK YÜKLEMEDE
  useEffect(() => {
    const savedLanguage = cookies.get('language') || 
                         navigator.language.split('-')[0] || 
                         'en';
    if (savedLanguage !== language) {
      setLanguage(savedLanguage);
    }
    setInitialLoad(false);
  }, []); // Boş bağımlılık dizisi - sadece mount anında çalışır

  // Dilleri API'den yükle - SADECE İLK YÜKLEMEDE
  useEffect(() => {
    api.listLanguages()
      .then(response => {
        if (response.data.status === 'ok') {
          setLanguages(response.data.data);
        }
      })
      .catch(error => {
        console.error('Diller yüklenirken hata:', error);
      });
  }, []); // api bağımlılığı kaldırıldı

  // Çevirileri yükle (dil değiştiğinde)
  useEffect(() => {
    if (!language || initialLoad) return; // İlk yükleme sırasında çalışmasın

    const controller = new AbortController();
    setLoading(true);
    
    api.getTranslations(language)
      .then(response => {
        if (response.data.status === 'ok') {
          setTranslations(response.data.data);
          
          const currentCookieLang = cookies.get('language');
          if (currentCookieLang !== language) {
            cookies.set('language', language, { 
              path: '/',
              sameSite: 'strict',
              secure: process.env.NODE_ENV === 'production'
            });
          }
        }
      })
      .catch(error => {
        console.error('Çeviriler yüklenirken hata:', error);
        // Hata durumunda translations'ı boş bir obje yap
        setTranslations({});
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [language]); // api ve initialLoad bağımlılıkları kaldırıldı

  const handleSetLanguage = React.useCallback((lang: string) => {
    setLanguage(lang);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        languages,
        translations,
        setLanguage: handleSetLanguage,
        loading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);